import { Map } from 'immutable';
import createCachedSelector from 're-reselect';
import { createSelector, Selector } from '@reduxjs/toolkit';

import type { ObjectsById } from './reducers/objects/types';
import type { ReduxState } from 'app/redux/types';

import { sortIdsByPriority } from 'app/lib/sortByPriorityImmutableData';

import type { RecordOfBettingEntitiesState } from './reducers/types/bettingRootReducer';

// ---

export type BettingEntitiesSelectors = ReturnType<typeof bettingEntitiesSelectors>;

export type MatchObjects = ObjectsById;


function bettingEntitiesSelectors(prefix: string | string[]) {
  const getter = buildGetter(prefix);

  function sports(state: ReduxState) {
    return getter(state, prefix).get('sports').get('byId');
  }

  function sportIds(state: ReduxState) {
    return getter(state, prefix).get('sports').get('allIds');
  }

  function tournamentUnions(state: ReduxState) {
    return getter(state, prefix).get('tournamentUnions').get('byId');
  }

  function tournaments(state: ReduxState) {
    return getter(state, prefix).get('tournaments').get('byId');
  }

  function tournamentStages(state: ReduxState) {
    return getter(state, prefix).get('tournamentStages').get('byId');
  }

  function matches(state: ReduxState) {
    return getter(state, prefix).get('matches').get('byId');
  }

  function objects(state: ReduxState) {
    return getter(state, prefix).get('objects').get('byId');
  }

  return {
    delta: (state: ReduxState) => getter(state, prefix).get('delta'),
    entityLoading: (state: ReduxState) => getter(state, prefix).get('loading'),
    entityRequested: (state: ReduxState) => getter(state, prefix).get('requested'),
    entityError: (state: ReduxState) => getter(state, prefix).get('error'),
    entitySignature: (state: ReduxState) => getter(state, prefix).get('signature'),

    sports,
    sportIds,
    tournamentUnions,
    tournaments,
    tournamentStages,
    tournamentStageIds: (state: ReduxState) => (
      getter(state, prefix).get('tournamentStages').get('allIds')
    ),
    matches,
    matchIds: (state: ReduxState) => (
      getter(state, prefix).get('matches').get('allIds')
    ),
    objects,

    getSortedSportIds: (state: ReduxState) => {
      return sortIdsByPriority(sportIds(state), sports(state));
    },

    getUnionsFromSportsSelector: createCachedSelector(
      (_state: ReduxState, sportId: number) => sportId,
      (state) => tournamentUnions(state),
      (sportId, tournamentUnions) => (
        tournamentUnions.filter((tournamentUnion) => tournamentUnion.sportId === sportId)
      ),
    )((_state: ReduxState, sportId) => sportId),

    getTournamentsFromSportsSelector: createCachedSelector(
      (_state: ReduxState, sportId: number) => sportId,
      (state) => tournaments(state),
      (sportId, tournaments) => {
        if (!sportId) {
          return tournaments;
        }

        return tournaments.filter((tournament) => (tournament.sportId === sportId));
      },
    )((_state: ReduxState, sportId) => sportId),

    getStagesFromSportsSelector: createCachedSelector(
      (_state: ReduxState, sportId: number) => sportId,
      (state) => tournamentStages(state),
      (sportId, tournamentStages) => (
        tournamentStages.filter((tournamentStage) => tournamentStage.sportId === sportId)
      ),
    )((_state: ReduxState, sportId) => sportId),

    getTournamentsFromUnionsSelector: createCachedSelector(
      (_state: ReduxState, tournamentUnionId: number) => tournamentUnionId,
      (state) => tournaments(state),
      (unionId, tournaments) => (
        tournaments.filter((tournament) => tournament.tournamentUnionId === unionId)
      ),
    )((_state: ReduxState, tournamentUnionId) => tournamentUnionId),

    getStagesFromUnionsSelector: createCachedSelector(
      (_state: ReduxState, tournamentUnionId: number) => tournamentUnionId,
      (state) => tournamentStages(state),
      (unionId, tournamentStages) => (
        tournamentStages.filter((tournamentStage) => tournamentStage.tournamentUnionId === unionId)
      ),
    )((_state: ReduxState, tournamentUnionId) => tournamentUnionId),

    getStagesFromTournamentsSelector: createCachedSelector(
      (_state: ReduxState, tournamentId: number) => tournamentId,
      (state) => tournamentStages(state),
      (tournamentId, tournamentStages) => (
        tournamentStages.filter((tournamentStage) => tournamentStage.tournamentId === tournamentId)
      ),
    )((_state: ReduxState, tournamentId) => tournamentId),

    createMatchObjectsSelector: (matchId: number): Selector<ReduxState, MatchObjects | null> => (
      createSelector(
        (state: ReduxState, props?: { matchId: number }) => {
          const id = matchId || props?.matchId;

          if (typeof id !== 'number') {
            return null;
          }

          return getter(state, prefix).get('matches').get('byId').get(id);
        },
        objects,
        (match, objectsById) => {
          if (!(match && match.objects)) {
            return null;
          }

          return match.objects.reduce((acc, id, key) => {
            const object = objectsById.get(id);

            return object ? acc.set(key, object) : acc;
          }, Map());
        },
      )
    ),

    isMatchStarted: (state: ReduxState, { matchId }: { matchId: number }): boolean => {
      const entities = getter(state, prefix);
      const match = entities.get('matches').get('byId').get(matchId);

      if (!match) {
        return false;
      }

      const periods = match && match.periods;
      return !!(periods && !periods.isEmpty()) || (match.scores && !match.scores.isEmpty());
    },

    // Detect whether a bet with the given id is `locked` respecting its parent nodes.
    isLockedBet: (state: ReduxState, matchId: number, betId: number) => {
      const node = getter(state, prefix);

      const match = node.get('matches').get('byId').get(matchId);

      if (!match) {
        return false;
      }

      const bet = match.bets.get(betId);

      if (!bet) {
        return false;
      }

      const stake = match.stakes.get(bet.stakeId);

      if (!stake) {
        return false;
      }

      return !(match.isOpen && stake.isOpen && bet.isOpen);
    },
  };
}

export default bettingEntitiesSelectors;

// ---

type BuildGetter = (state: ReduxState, prefix: string | string[]) => RecordOfBettingEntitiesState;

function buildGetter(prefix: string | string[]): BuildGetter {
  if (!Array.isArray(prefix)) {
    return getStateNode as BuildGetter;
  }

  switch (prefix.length) {
    case 1:
      return getStateNodeUnrolled1 as BuildGetter;

    case 2:
      return getStateNodeUnrolled2 as BuildGetter;

    case 3:
      return getStateNodeUnrolled3 as BuildGetter;

    case 4:
      return getStateNodeUnrolled4 as BuildGetter;

    default:
      return getStateNodeUnrolledN as unknown as BuildGetter;
  }
}

function getStateNode(state: ReduxState, prefix: string) {
  return state[prefix as keyof typeof state];
}

function getStateNodeUnrolled1(state: ReduxState, prefix: string[]) {
  return state[prefix[0] as keyof typeof state];
}

function getStateNodeUnrolled2(state: ReduxState, prefix: string[]) {
  let result = getStateNodeUnrolled1(state, prefix);

  if (typeof result.get === 'function') {
    result = result.get(prefix[1]);
  }
  else {
    result = result[prefix[1]];
  }

  return result;
}

function getStateNodeUnrolled3(state: ReduxState, prefix: string[]) {
  let result = getStateNodeUnrolled2(state, prefix);

  if (typeof result.get === 'function') {
    result = result.get(prefix[2]);
  }
  else {
    result = result[prefix[2]];
  }

  return result;
}

function getStateNodeUnrolled4(state: ReduxState, prefix: string[]) {
  let result = getStateNodeUnrolled3(state, prefix);

  if (typeof result.get === 'function') {
    result = result.get(prefix[3]);
  }
  else {
    result = result[prefix[3]];
  }

  return result;
}

function getStateNodeUnrolledN(state: ReduxState, prefix: string[]) {
  return prefix.reduce((stateNode, key) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return (typeof stateNode.get === 'function') ? stateNode.get(key) : stateNode[key];
  }, state);
}
